import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { AdminIconButton } from "@/components/console/AdminIconButton";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableEmptyRow,
  DataTableHead,
  DataTableHeadCell,
  DataTableRow,
  DataTableScroll,
} from "@/components/ui/DataTable";
import {
  LibraryCollection,
  LibraryCollectionFooter,
  LibraryCollectionTableShell,
  LibraryCollectionToolbar,
} from "@/components/ui/LibraryCollection";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { errorMessage } from "@/lib/api";
import { useAdminAthletes } from "@/features/admin/hooks/useAdminData";
import { sortSeasonsNewestFirst } from "@/features/admin/components/SeasonTeamGroups";
import { PageNavigation } from "@/features/collections/CollectionControls";
import { useAuthStore } from "@/stores/auth";
import type {
  AthleteDirectoryEntry,
  AthleteDirectoryStatusFilter,
  Season,
} from "@/lib/types";
import "@/features/admin/match-library.css";
import { AthleteComposerDialog } from "../components/AthleteComposerDialog";
import {
  ArchiveAthleteDialog,
  DeleteAthleteDialog,
  useRestoreAthlete,
} from "../components/AthleteLifecycleDialogs";
import { AthleteRowActions } from "../components/AthleteRowActions";
import { AthleteRowEditDialog } from "../components/AthleteRowEditDialog";
import { RosterAssignmentDialog } from "../components/assignmentDialogs";
import { useRosterableTeams } from "../hooks/useAdminDirectoryLookups";
import {
  AssignmentChip,
  AthleteStatusBadge,
  hasSeasonAssignment,
  searchDraftForUrl,
} from "../athleteDisplay";

/**
 * `/admin/athletes` — the club athlete directory (#908, plan §7 "Club
 * console → Athletes"). One record per athlete in the club; each season
 * team's roster is a set of assignments of athletes.
 *
 * Presentation reuses the Match Library collection (search + filter
 * toolbar, table shell, footer pagination) so the two admin lists read
 * the same.
 *
 * Search (`?q=`), status (`?status=`) and season (`?season=`) live in the
 * URL (deep-link / back-nav); the search box is debounced into it. The
 * status filter maps onto the API's single `status` (Active is the
 * default and is left out of the URL). The season filter is applied
 * client-side: athletes holding an assignment on a team of that season.
 *
 * Disclosure (§5.3, D14): name, status and team/season assignments; the
 * birth year shows only for the viewers the server gives it to (club
 * admins and the athlete's own team staff, D11) — the page never guesses
 * rights, it renders what the API returned.
 *
 * Row quick actions (the Match Library row pattern): Edit, and a More menu
 * with Open, Roster on a team, Archive / Restore and the guarded Delete —
 * each shown only when the row's `can_edit` / `can_archive` allow it.
 */

type RowDialog =
  | { kind: "edit" | "roster" | "archive" | "delete"; athlete: AthleteDirectoryEntry }
  | null;

const SEARCH_DEBOUNCE_MS = 250;
const PAGE_SIZE_OPTIONS = [10, 25, 50].map((size) => ({
  value: String(size),
  label: String(size),
}));
/** Stable empty identity so the `me.seasons` selector doesn't re-fire memos. */
const EMPTY_SEASONS: Season[] = [];

const STATUS_OPTIONS: { value: AthleteDirectoryStatusFilter; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "merged", label: "Merged" },
];

export function AthletesPage() {
  const search = useSearch({ from: "/admin/athletes" });
  const navigate = useNavigate({ from: "/admin/athletes" });
  const urlQuery = search.q ?? "";
  const status: AthleteDirectoryStatusFilter = search.status ?? "active";

  // Season options come from the shared `me.seasons` list (the source the
  // Match Library season filter uses), newest first.
  const seasons = useAuthStore((s) => s.me?.seasons ?? EMPTY_SEASONS);
  const seasonOptions = useMemo(
    () => [
      { value: "all", label: "All seasons" },
      ...sortSeasonsNewestFirst(seasons).map((s) => ({
        value: s.id,
        label: s.name,
      })),
    ],
    [seasons],
  );
  const seasonId = search.season ?? "all";
  const seasonName = seasons.find((s) => s.id === seasonId)?.name ?? null;

  // The search box is local state pushed into the URL after a short pause;
  // a URL change from outside (back / forward, deep link) wins over it.
  const [draft, setDraft] = useState(urlQuery);
  useEffect(() => {
    setDraft((current) => searchDraftForUrl(current, urlQuery));
  }, [urlQuery]);
  useEffect(() => {
    if (draft.trim() === urlQuery) return;
    const handle = window.setTimeout(() => {
      void navigate({
        search: (prev) => ({ ...prev, q: draft.trim() || undefined }),
        replace: true,
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft, urlQuery, navigate]);

  const setStatus = (next: string) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        status:
          next === "archived" || next === "merged" ? next : undefined,
      }),
      replace: false,
    });
  };
  const setSeason = (next: string) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        season: next === "all" ? undefined : next,
      }),
      replace: false,
    });
  };
  const hasActiveFilters =
    draft.trim() !== "" || status !== "active" || seasonId !== "all";
  const clearFilters = () => {
    setDraft("");
    void navigate({ search: {}, replace: false });
  };

  const athletesQuery = useAdminAthletes(urlQuery, status);
  const rows = useMemo(() => athletesQuery.data ?? [], [athletesQuery.data]);
  const visible = useMemo(
    () =>
      seasonId === "all"
        ? rows
        : rows.filter((a) => hasSeasonAssignment(a.assignments, seasonId)),
    [rows, seasonId],
  );

  const [pageSize, setPageSize] = useState("25");
  const pageSizeNumber = Number(pageSize);
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [urlQuery, status, seasonId, pageSize]);
  const totalPages = Math.max(1, Math.ceil(visible.length / pageSizeNumber));
  const currentPage = Math.min(page, totalPages);
  const pageRows = visible.slice(
    (currentPage - 1) * pageSizeNumber,
    currentPage * pageSizeNumber,
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [rowDialog, setRowDialog] = useState<RowDialog>(null);
  const { restore, pendingId: restoringId } = useRestoreAthlete();
  const isLoading = athletesQuery.isLoading;
  const columnCount = 4;
  const openAthlete = (athleteId: string) =>
    void navigate({ to: "/admin/athletes/$athleteId", params: { athleteId } });

  const emptyCopy = (() => {
    const scope = seasonName ? ` in ${seasonName}` : "";
    if (urlQuery) return `No ${status} athlete${scope} matches “${urlQuery}”.`;
    if (seasonName || hasActiveFilters)
      return `No ${status} athletes${scope}. Clear filters to see every athlete.`;
    return `No ${status} athletes yet.`;
  })();

  return (
    <div data-testid="admin-athletes" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Club setup"
        title="Athletes"
        description="One record per athlete in the club. A season team's roster is a set of assignments, so an athlete keeps their history from one season to the next."
        actions={
          <AdminIconButton
            icon={Plus}
            label="New athlete"
            visibleLabel
            size="md"
            tone="accent"
            onClick={() => setCreateOpen(true)}
            data-testid="admin-athletes-new"
          />
        }
      />

      {athletesQuery.error ? (
        <p
          data-testid="admin-athletes-error"
          className="font-mono text-xs text-destructive"
        >
          {errorMessage(athletesQuery.error)}
        </p>
      ) : null}

      <LibraryCollection className="admin-match-library-pane admin-library-pane">
        <LibraryCollectionToolbar
          role="group"
          aria-label="Search and filter athletes"
        >
          <label className="admin-match-search-field library-search-field">
            <span>Quick search</span>
            <span className="admin-match-search-control library-search-control">
              <Search size={16} aria-hidden="true" />
              <input
                type="search"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Search by name"
                aria-label="Search athletes"
                data-testid="admin-athletes-search"
              />
            </span>
          </label>
          <label className="admin-match-filter-field library-filter-field">
            <span>Status</span>
            <TkticSelect
              value={status}
              onChange={setStatus}
              options={STATUS_OPTIONS}
              size="sm"
              ariaLabel="Filter athletes by status"
              data-testid="admin-athletes-status"
            />
          </label>
          <label className="admin-match-filter-field library-filter-field">
            <span>Season</span>
            <TkticSelect
              value={seasonId}
              onChange={setSeason}
              options={seasonOptions}
              size="sm"
              ariaLabel="Filter athletes by season"
              data-testid="admin-athletes-season"
            />
          </label>
          <button
            type="button"
            className="admin-match-clear-filters library-clear justify-self-end"
            disabled={!hasActiveFilters}
            onClick={clearFilters}
            data-testid="admin-athletes-clear"
          >
            Clear filters
          </button>
        </LibraryCollectionToolbar>

        <LibraryCollectionTableShell>
          <DataTableScroll>
            <DataTable
              data-testid="admin-athletes-table"
              className="library-collection-table"
            >
              <colgroup>
                {[30, 12, 40, 18].map((width, index) => (
                  <col key={index} style={{ width: `${width}%` }} />
                ))}
              </colgroup>
              <DataTableHead>
                <DataTableRow interactive={false}>
                  <DataTableHeadCell>Athlete</DataTableHeadCell>
                  <DataTableHeadCell>Status</DataTableHeadCell>
                  <DataTableHeadCell>Assignments</DataTableHeadCell>
                  <DataTableHeadCell align="right">Actions</DataTableHeadCell>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {isLoading ? (
                  <DataTableEmptyRow
                    colSpan={columnCount}
                    data-testid="admin-athletes-loading"
                  >
                    Loading athletes…
                  </DataTableEmptyRow>
                ) : pageRows.length === 0 ? (
                  <DataTableEmptyRow
                    colSpan={columnCount}
                    data-testid="admin-athletes-empty"
                  >
                    {emptyCopy}
                  </DataTableEmptyRow>
                ) : (
                  pageRows.map((athlete) => {
                    const assignments = athlete.assignments ?? [];
                    return (
                      <DataTableRow
                        key={athlete.id}
                        data-testid="admin-athlete-row"
                        data-athlete-id={athlete.id}
                        className="admin-library-row"
                        onClick={(event) => {
                          if (
                            (event.target instanceof Element &&
                              event.target.closest(
                                'button,a,input,[role="menuitem"]',
                              )) ||
                            window.getSelection()?.toString()
                          )
                            return;
                          openAthlete(athlete.id);
                        }}
                      >
                        <DataTableCell>
                          <div className="admin-library-identity">
                            <Link
                              to="/admin/athletes/$athleteId"
                              params={{ athleteId: athlete.id }}
                              className="admin-library-title"
                              data-testid="admin-athlete-row-name"
                            >
                              <strong>{athlete.display_name}</strong>
                            </Link>
                            {athlete.birth_year ? (
                              <small data-testid="admin-athlete-row-born">
                                Born {athlete.birth_year}
                              </small>
                            ) : null}
                          </div>
                        </DataTableCell>
                        <DataTableCell>
                          <AthleteStatusBadge status={athlete.status} />
                        </DataTableCell>
                        <DataTableCell>
                          {assignments.length === 0 ? (
                            <span className="text-xs text-ink-muted">
                              No assignments
                            </span>
                          ) : (
                            <div className="flex flex-wrap gap-1.5">
                              {assignments.map((assignment) => (
                                <AssignmentChip
                                  key={assignment.player_id}
                                  assignment={assignment}
                                />
                              ))}
                            </div>
                          )}
                        </DataTableCell>
                        <DataTableCell>
                          <AthleteRowActions
                            athlete={athlete}
                            restoring={restoringId === athlete.id}
                            onEdit={() =>
                              setRowDialog({ kind: "edit", athlete })
                            }
                            onRoster={() =>
                              setRowDialog({ kind: "roster", athlete })
                            }
                            onArchive={() =>
                              setRowDialog({ kind: "archive", athlete })
                            }
                            onRestore={() => void restore(athlete)}
                            onDelete={() =>
                              setRowDialog({ kind: "delete", athlete })
                            }
                          />
                        </DataTableCell>
                      </DataTableRow>
                    );
                  })
                )}
              </DataTableBody>
            </DataTable>
          </DataTableScroll>
        </LibraryCollectionTableShell>

        {!isLoading && visible.length > 0 ? (
          <LibraryCollectionFooter>
            <span data-testid="admin-athletes-page-summary">
              {(currentPage - 1) * pageSizeNumber + 1}–
              {Math.min(currentPage * pageSizeNumber, visible.length)} of{" "}
              {visible.length} athlete{visible.length === 1 ? "" : "s"}
            </span>
            <div>
              <label className="library-filter-field">
                <span>Per page</span>
                <div>
                  <TkticSelect
                    value={pageSize}
                    onChange={setPageSize}
                    options={PAGE_SIZE_OPTIONS}
                    size="sm"
                    ariaLabel="Athletes per page"
                    data-testid="admin-athletes-page-size"
                  />
                </div>
              </label>
              <PageNavigation
                page={currentPage}
                pages={totalPages}
                onChange={setPage}
              />
            </div>
          </LibraryCollectionFooter>
        ) : null}
      </LibraryCollection>

      {createOpen ? (
        <AthleteComposerDialog
          mode="create"
          onClose={() => setCreateOpen(false)}
          onSaved={(athlete) => {
            setCreateOpen(false);
            openAthlete(athlete.id);
          }}
        />
      ) : null}
      {rowDialog?.kind === "edit" ? (
        <AthleteRowEditDialog
          athlete={rowDialog.athlete}
          onClose={() => setRowDialog(null)}
        />
      ) : null}
      {rowDialog?.kind === "roster" ? (
        <RowRosterDialog
          athlete={rowDialog.athlete}
          onClose={() => setRowDialog(null)}
        />
      ) : null}
      {rowDialog?.kind === "archive" ? (
        <ArchiveAthleteDialog
          athlete={rowDialog.athlete}
          onClose={() => setRowDialog(null)}
        />
      ) : null}
      {rowDialog?.kind === "delete" ? (
        <DeleteAthleteDialog
          athlete={rowDialog.athlete}
          onClose={() => setRowDialog(null)}
        />
      ) : null}
    </div>
  );
}

function RowRosterDialog({
  athlete,
  onClose,
}: {
  athlete: AthleteDirectoryEntry;
  onClose: () => void;
}) {
  const { teams, isLoading } = useRosterableTeams();
  return (
    <RosterAssignmentDialog
      athlete={athlete}
      teams={teams}
      teamsLoading={isLoading}
      assignments={athlete.assignments ?? []}
      onClose={onClose}
      onSaved={onClose}
    />
  );
}
